<script setup lang="ts">
import {inject, ref} from "vue";
import {PerfectScrollbar} from "vue3-perfect-scrollbar";
import {useRouter} from "vue-router";
import {useConfirm} from "primevue/useconfirm";
import Str from "@/Utils/Str";
import {deleteQuestion, getQuestion as apiGetQuestion} from "@/Services/Admin/admin-questions";
import {PageHeading, PageLoading} from "@/Components/Global";
import {Button, Card, Menu} from "@/Components/Library";
import type dayjs from "dayjs";

const $date = inject("date") as typeof dayjs;
const router = useRouter();
const confirm = useConfirm();

const question = ref();
const questionMenuContainer = ref<Menu>();
const questionMenuItems = [
    {
        label: "Edit",
        icon: "pepicons-pencil:dots-y",
        command: () => {
            router.push({
                name: "admin.questions.edit",
                params: {question: router.currentRoute.value.params.question},
            });
        },
    },
    {
        label: "Archive",
        icon: "prime:trash",
        class: "text-red-600 hover:bg-red-100",
        command: () => archiveQuestion(),
    },
];

const getQuestion = async () =>
    apiGetQuestion(router.currentRoute.value.params.question).then(({data}) => (question.value = data));
getQuestion();

const archiveQuestion = () => {
    confirm.require({
        message: "Are you sure you want to archive this question?",
        accept: async () => {
            await deleteQuestion(question.value.id);
            await router.push({name: "admin.questions.index"});
        },
        acceptClass: "bg-red-600 text-white px-2 py-1 rounded-lg hover:brightness-110",
        rejectClass: "px-2 py-1 hover:brightness-110",
    });
};
</script>
<template>
    <div class="mx-auto w-full max-w-5xl">
        <div class="m-container mb-3">
            <PageHeading title="Question">
                <div class="ml-auto flex gap-2">
                    <Button
                        :icon="{
                            icon: 'pepicons-pencil:dots-y',
                            width: '24',
                        }"
                        class="!ring-0"
                        @click.stop="questionMenuContainer.menu.toggle($event)"
                    />
                    <Menu ref="questionMenuContainer" popup :model="questionMenuItems" />
                </div>
            </PageHeading>
        </div>
        <PageLoading v-if="!question" />
        <div v-else class="flex flex-col gap-4">
            <div class="flex flex-col gap-4 md:flex-row">
                <Card class="grow">
                    <template #title>Details</template>
                    <table v-if="question">
                        <tbody>
                            <tr>
                                <td>ID</td>
                                <td>{{ question.id }}</td>
                            </tr>
                            <tr>
                                <td>Question</td>
                                <td>{{ question.title }}</td>
                            </tr>
                            <tr>
                                <td>Created At</td>
                                <td>
                                    {{ $date(question.created_at).format("DD/MM/YYYY HH:mm") }}
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </Card>
                <Card class="md:w-fit">
                    <template #title>Params</template>
                    <table>
                        <tbody>
                            <template v-for="[key, value] in Object.entries(question.params)" :key="key">
                                <tr>
                                    <td>{{ Str.of(key).title() }}</td>
                                    <td>
                                        <span v-if="typeof value === 'boolean'">
                                            {{ value ? "Yes" : "No" }}
                                        </span>
                                        <span v-else>
                                            {{ value }}
                                        </span>
                                    </td>
                                </tr>
                            </template>
                        </tbody>
                    </table>
                </Card>
            </div>
            <!--            <Card>-->
            <!--                <template #title>Tags</template>-->
            <!--                <div v-if="question.tags.length" class="flex flex-wrap gap-2 pt-2">-->
            <!--                    <Tag v-for="tag in question.tags" :key="tag.id" :value="tag.name" />-->
            <!--                </div>-->
            <!--                <div v-else class="p-2 text-center">No tags attached.</div>-->
            <!--            </Card>-->
            <Card v-if="question.options.length">
                <template #title>Options</template>
                <PerfectScrollbar class="flex max-h-96 flex-col divide-y overflow-y-scroll">
                    <template v-for="(option, i) in question.options" :key="option.id">
                        <div class="relative flex w-full transition-colors duration-300 hover:bg-stone-50">
                            <div class="flex w-full items-center gap-2 py-1 pr-2">
                                <div class="flex grow items-center gap-2">
                                    <div class="h-6 w-6 rounded-full bg-askr-green text-center text-white">
                                        {{ i + 1 }}
                                    </div>
                                    <h3 class="grow font-normal">{{ Str.of(option.value).title() }}</h3>
                                </div>
                                <div>
                                    <!-- TODO: Responses count -->
                                </div>
                            </div>
                        </div>
                    </template>
                </PerfectScrollbar>
            </Card>
            <!-- TODO: Responses -->
        </div>
    </div>
</template>
<style scoped>
td {
    @apply py-1.5 pr-8 align-top;
}

tr > td:first-child {
    @apply font-bold;
}
</style>
